import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { getFlowDiagram } from "./flowApi";
import { renderFlowSvg } from "./flowSvg";

function figureFromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  return target.closest("figure.rte-flow");
}

function svgPixelSize(svgEl) {
  const box = svgEl.viewBox?.baseVal;
  const width = box?.width || Number(svgEl.getAttribute("width")) || 0;
  const height = box?.height || Number(svgEl.getAttribute("height")) || 0;
  return { width, height };
}

function FlowViewStage({ svg }) {
  const viewportRef = useRef(null);
  const scaleRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const fittedRef = useRef(1);
  const movedRef = useRef(false);
  const dragRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [fitted, setFitted] = useState(1);
  const [ready, setReady] = useState(false);
  const [dragging, setDragging] = useState(false);

  const commitView = (nextScale, nextPan, nextFit) => {
    scaleRef.current = nextScale;
    panRef.current = nextPan;
    if (nextFit != null) fittedRef.current = nextFit;
    setScale(nextScale);
    setPan(nextPan);
    if (nextFit != null) setFitted(nextFit);
  };

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    movedRef.current = false;
    setReady(false);

    const fitDiagram = () => {
      const svgEl = viewport.querySelector("svg");
      if (!svgEl) return;
      const { width, height } = svgPixelSize(svgEl);
      const viewW = viewport.clientWidth;
      const viewH = viewport.clientHeight;
      if (!width || !height || viewW < 8 || viewH < 8) return;
      const availW = Math.max(1, viewW - 32);
      const availH = Math.max(1, viewH - 32);
      const fit = Math.min(1, availW / width, availH / height);
      const safe = fit > 0 && Number.isFinite(fit) ? fit : 1;
      if (movedRef.current) return;
      commitView(safe, {
        x: (viewW - width * safe) / 2,
        y: (viewH - height * safe) / 2,
      }, safe);
      setReady(true);
    };

    fitDiagram();
    const observer = new ResizeObserver(fitDiagram);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [svg]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const onWheel = (event) => {
      const svgEl = viewport.querySelector("svg");
      if (!svgEl) return;
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      const current = scaleRef.current;
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      const min = Math.min(fittedRef.current, 0.2);
      const next = Math.min(4, Math.max(min, current * factor));
      if (next === current) return;
      const origin = panRef.current;
      const ratio = next / current;
      movedRef.current = true;
      commitView(next, {
        x: px - (px - origin.x) * ratio,
        y: py - (py - origin.y) * ratio,
      });
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [svg]);

  const canPan = scale > fitted + 0.01;

  const onPointerDown = (event) => {
    if (event.button !== 0 || !canPan) return;
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    movedRef.current = true;
    commitView(scaleRef.current, {
      x: drag.panX + (event.clientX - drag.x),
      y: drag.panY + (event.clientY - drag.y),
    });
  };

  const endDrag = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <div
      ref={viewportRef}
      className={`flow-view-viewport${canPan ? " is-pannable" : ""}${dragging ? " is-dragging" : ""}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        className="flow-view-stage"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          visibility: ready ? "visible" : "hidden",
        }}
        dangerouslySetInnerHTML={{ __html: svg || "" }}
      />
    </div>
  );
}

export default function FlowDiagramChrome() {
  const [view, setView] = useState(null);

  useEffect(() => {
    const onClick = (event) => {
      const target = event.target;
      if (
        !(target instanceof Element)
        || target.closest(".rte-media-chrome, .rte-flow-edit, .flow-view-modal, .flow-modal, figcaption.rte-caption")
      ) return;
      const fig = figureFromEvent(event);
      if (!fig || fig.classList.contains("rte-flow-missing")) return;
      event.preventDefault();
      event.stopPropagation();
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      const id = fig.getAttribute("data-rte-flow-id");
      const svg = fig.querySelector("svg")?.outerHTML || "";
      setView({ id: String(id), title: "Diagram", svg, missing: !svg });
      getFlowDiagram(id).then((record) => {
        setView((current) => (
          current && String(current.id) === String(id)
            ? { id: String(id), title: record.title || "Diagram", svg: renderFlowSvg(record), missing: false }
            : current
        ));
      }).catch((err) => {
        if (err.status !== 404) return;
        setView((current) => (
          current && String(current.id) === String(id)
            ? { id: String(id), title: "Diagram", svg: "", missing: true }
            : current
        ));
      });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <>
      <Modal
        show={Boolean(view)}
        onHide={() => setView(null)}
        size="xl"
        centered
        contentClassName="flow-view-modal"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title as="h2" className="h5 mb-0">{view?.title || "Diagram"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="flow-view-body">
          {view?.missing ? (
            <p className="rte-flow-missing">Diagram unavailable</p>
          ) : (
            <FlowViewStage svg={view?.svg || ""} />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
